import { useState, useEffect } from "react";
import Modal, { Field, inputCls, PrimaryBtn, GhostBtn } from "@/components/Modal";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { toast } from "sonner";

const STATUSES = ["confirmed", "completed", "cancelled", "no_show"];

export default function AppointmentDialog({ open, onClose, onSaved, customers, staff, services, initial, defaultDate }) {
  const { t } = useI18n();
  const empty = {
    customer_id: "", staff_id: "", service_id: "",
    date: defaultDate || new Date().toISOString().slice(0, 10),
    time: "10:00", status: "confirmed", notes: "", location_id: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (open) api.get("/locations").then(({ data }) => setLocations(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) {
      if (initial) {
        const d = new Date(initial.start);
        setForm({
          customer_id: initial.customer_id, staff_id: initial.staff_id, service_id: initial.service_id,
          date: d.toISOString().slice(0, 10),
          time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
          status: initial.status, notes: initial.notes || "", location_id: initial.location_id || "",
        });
      } else {
        setForm({ ...empty, date: defaultDate || empty.date });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, defaultDate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.customer_id || !form.staff_id || !form.service_id) {
      toast.error(t.dlg.selectErr);
      return;
    }
    setSaving(true);
    const start = new Date(`${form.date}T${form.time}:00`).toISOString();
    const payload = { customer_id: form.customer_id, staff_id: form.staff_id, service_id: form.service_id, start, status: form.status, notes: form.notes, location_id: form.location_id || null };
    try {
      if (initial) await api.put(`/appointments/${initial.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success(initial ? t.dlg.updated : t.dlg.saved);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t.dlg.failed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open} onClose={onClose} testid="appointment-dialog"
      title={initial ? t.dlg.editTitle : t.dlg.newTitle}
      footer={
        <>
          <GhostBtn onClick={onClose} data-testid="appt-cancel">{t.btn.cancel}</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving} data-testid="appt-save">{saving ? t.btn.saving : t.btn.save}</PrimaryBtn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t.dlg.customer}>
          <select data-testid="appt-customer" className={inputCls} value={form.customer_id} onChange={set("customer_id")}>
            <option value="">{t.dlg.selectCustomer}</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label={t.dlg.service}>
          <select data-testid="appt-service" className={inputCls} value={form.service_id} onChange={set("service_id")}>
            <option value="">{t.dlg.selectService}</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.duration_min}min</option>)}
          </select>
        </Field>
        <Field label={t.dlg.staff}>
          <select data-testid="appt-staff" className={inputCls} value={form.staff_id} onChange={set("staff_id")}>
            <option value="">{t.dlg.selectStaff}</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        {locations.length > 0 && (
          <Field label={t.loc.assign}>
            <select data-testid="appt-location" className={inputCls} value={form.location_id || ""} onChange={set("location_id")}>
              <option value="">{t.loc.noLoc}</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label={t.dlg.date}><input data-testid="appt-date" type="date" className={inputCls} value={form.date} onChange={set("date")} /></Field>
          <Field label={t.dlg.time}><input data-testid="appt-time" type="time" className={inputCls} value={form.time} onChange={set("time")} /></Field>
        </div>
        {initial && (
          <Field label={t.dlg.status}>
            <select data-testid="appt-status" className={inputCls} value={form.status} onChange={set("status")}>
              {STATUSES.map((s) => <option key={s} value={s}>{t.st[s]}</option>)}
            </select>
          </Field>
        )}
        <Field label={t.dlg.notes}>
          <textarea data-testid="appt-notes" className={inputCls} rows={2} value={form.notes} onChange={set("notes")} placeholder={t.dlg.notesPh} />
        </Field>
      </div>
    </Modal>
  );
}
